import type { BulkFinalFieldsRequestModel } from '@/entities/optimization'
import type { FilterRuleModel } from '@/shared/generated'

import React from 'react'
import { Undo2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useBulkFinalFieldsMutation } from '@/entities/optimization'

import { Operators } from '@/shared/components/query-builder'
import { Button } from '@/shared/components/ui/button'
import { ButtonGroup } from '@/shared/components/ui/button-group'
import { Spinner } from '@/shared/components/ui/spinner'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { ampli } from '@/shared/lib/ampli'
import { useStore } from '@/shared/lib/datasheet/store'
import Sentry from '@/shared/lib/sentry'

export const GlobalAssortmentSaveChanges = () => {
	const { t } = useTranslation()

	const setLocalData = useStore((state) => state.setLocalData)
	const localData = useStore((state) => state.localData)

	const bulkFinalFields = useBulkFinalFieldsMutation({
		onSuccess: () => {
			setLocalData({})
		},
	})
	const isDirty = Object.keys(localData).length > 0
	if (!isDirty) return null
	const handleSaveChanges = () => {
		ampli.globalAssortmentSaveChanges()
		toast.promise(
			bulkFinalFields.mutateAsync(createBulkFinalFieldsRequest(localData)),
			{
				loading: t('saving_changes.proccess', {
					defaultValue: 'Saving changes...',
				}),
				success: t('saving_changes.success', {
					defaultValue: 'Changes saved successfully',
				}),
				error: (error) => {
					Sentry.captureException(error)

					return t('saving_changes.error', {
						defaultValue: 'Error while saving changes',
					})
				},
			}
		)
	}

	return (
		<>
			<ButtonGroup>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button size='icon' onClick={() => setLocalData({})}>
							<Undo2Icon />
						</Button>
					</TooltipTrigger>
					<TooltipContent>{t('app.revert_changes')}</TooltipContent>
				</Tooltip>
			</ButtonGroup>
			<ButtonGroup>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							variant='warning'
							disabled={bulkFinalFields.isPending}
							onClick={handleSaveChanges}
						>
							{bulkFinalFields.isPending ? (
								<Spinner />
							) : (
								t('general_save_changes')
							)}
						</Button>
					</TooltipTrigger>
					<TooltipContent>{t('general_save_changes')}</TooltipContent>
				</Tooltip>
			</ButtonGroup>
		</>
	)
}

export const createBulkFinalFieldsRequest = (
	localData: Record<string, object>
) => {
	const patches: BulkFinalFieldsRequestModel = []
	Object.entries(localData).forEach(([key, value]: [string, object]) => {
		const [ogId, lineId, sku] = key.split('__')
		if (sku) {
			const patch = {
				filters: [
					{
						name: 'sku',
						operation: Operators.IS,
						value: sku,
					} as FilterRuleModel,
					{
						name: 'optimization_group_id',
						operation: Operators.IS,
						value: ogId,
					} as FilterRuleModel,
				],
				final_fields: {
					...(localData[`${ogId}__${lineId}__`] || {}),
					...value,
				},
			}
			patches.push(patch)
		}
	})

	return patches
}
